import { css } from 'emotion';
import React, { ComponentProps, useMemo } from 'react';

import { DepartmentType } from '../../../../../../../gql/gql-types';
import { Caption } from '../../../../../../components/Caption';
import { Breakpoint } from '../../../../../../constants/Style';
import { Department } from '../../../../../../domains/reservation/Department';
import { gutter } from '../../../../../../helpers/Style';
import { useContext } from '../../../../../../hooks/useContext';
import { SessionStore } from '../../../../../../stores/SessionStore';
import { EmptyCards } from '../../../../../Root/components/EmptyCards';
import { ExternalDivision, ExternalDivisionLink, ExternalDivisions } from '../../constants/ExternalDivisions';
import { DepartmentCard } from './DepartmentCard';
import { ExternalDivisionCard } from './ExternalDivisionCard';

type DepartmentCardProps = Pick<ComponentProps<typeof DepartmentCard>, 'department' | 'nearest'>;

type ClinicProps = {
  departments: DepartmentCardProps[];
  onClick?: (department: Department) => void;
  selectedId?: string;
  online: boolean;
};

type Props = {
  clinics: Clinic[];
  onClick?: (department: Department) => void;
  selectedId?: string;
  online: boolean;
  /** 外部サイトへの導線セクション。表示位置は各定義の insertAfterClinicName に従う */
  externalDivisions?: ExternalDivision[];
};

type Clinic = {
  name: string;
  departments: DepartmentCardProps[];
};

/** 見出し＋カード群という単位で、クリニックと外部リンクを同列に扱うための型 */
type Section =
  | { kind: 'clinic'; name: string; departments: DepartmentCardProps[] }
  | { kind: 'external'; name: string; links: ExternalDivisionLink[] };

/**
 * クリニックの並びに外部リンクセクションを差し込んだ表示順を組み立てる。
 * 差し込み先のクリニックが存在しない定義は、取りこぼさないよう末尾に置く。
 */
const buildSections = (clinics: Clinic[], externalDivisions: ExternalDivision[]): Section[] => {
  const inserted = new Set<ExternalDivision>();

  const sections = clinics.flatMap<Section>(clinic => {
    const followings = externalDivisions.filter(division => division.insertAfterClinicName === clinic.name);
    followings.forEach(division => inserted.add(division));
    return [
      { kind: 'clinic', name: clinic.name, departments: clinic.departments },
      ...followings.map<Section>(division => ({ kind: 'external', name: division.name, links: division.links })),
    ];
  });

  const rests = externalDivisions
    .filter(division => !inserted.has(division))
    .map<Section>(division => ({ kind: 'external', name: division.name, links: division.links }));

  return [...sections, ...rests];
};

const clinicSubheaderStyle = css`
  margin: ${gutter(4)} 0;
`;

export const ClinicDepartmentCardLayout = ({
  clinics,
  onClick,
  selectedId,
  online,
  externalDivisions = ExternalDivisions,
}: Props) => {
  const sections = useMemo(() => buildSections(clinics, externalDivisions), [clinics, externalDivisions]);

  return (
    <div>
      {clinics.length > 0 ? (
        sections.map((section, key) => (
          <div key={key} className={departmentCardStyle}>
            <div className={clinicSubheaderStyle}>
              <Caption type="subheader">{section.name}</Caption>
            </div>
            {section.kind === 'clinic' ? (
              <DepartmentCardLayout
                departments={section.departments}
                onClick={onClick}
                selectedId={selectedId}
                online={online}
              />
            ) : (
              <ExternalDivisionCardLayout links={section.links} />
            )}
          </div>
        ))
      ) : (
        <div>
          <EmptyCards>
            <Caption type="subheader">空き枠情報を読み込んでいます</Caption>
          </EmptyCards>
        </div>
      )}
    </div>
  );
};

const departmentCardStyle = css`
  margin-bottom: ${gutter(2)};
`;

/**
 * 診療科の一覧を受け取り、二列のグリッド状に配置するコンポーネント。
 */
const DepartmentCardLayout = ({ departments, onClick, selectedId, online }: ClinicProps) => {
  const session = useContext(SessionStore.Context);
  return (
    <div className={grid}>
      {departments.map((cell, inx) => {
        const disabled =
          (!!online && !cell.department.online) ||
          (cell.department.type !== DepartmentType.Checkup && !!session.user?.userEntity?.accountRestricted);
        return (
          <DepartmentCard
            key={inx}
            nearest={!session.user?.userEntity?.accountRestricted ? cell.nearest : undefined}
            onClick={department => onClick?.(department)}
            department={cell.department}
            selected={cell.department.id === selectedId}
            disabled={disabled}
          />
        );
      })}
    </div>
  );
};

/**
 * 外部リンクを診療科と同じグリッドに配置するコンポーネント。
 */
const ExternalDivisionCardLayout = ({ links }: { links: ExternalDivisionLink[] }) => (
  <div className={grid}>
    {links.map((link, inx) => (
      <ExternalDivisionCard key={inx} link={link} />
    ))}
  </div>
);

const grid = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${gutter(4)};

  @media (max-width: ${Breakpoint.ScreenXs}px) {
    grid-template-columns: 1fr;
    grid-gap: ${gutter(3)};
  }
`;
